import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { DriveMoveExecutionCoordinator } from './drive-move-execution.coordinator';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveItem } from './drive-viewer.models';
import { DriveMoveState, DriveWorkspaceState } from './drive-viewer.state';

export class DriveDragDropCoordinator {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly state: DriveMoveState,
    private readonly selection: DriveSelectionQuery,
    private readonly execution: DriveMoveExecutionCoordinator,
    private readonly logger: FrontendLoggerService
  ) {}

  fileDragStarted(item: DriveItem): void { this.state.isDraggingFile = item.type !== 'folder'; }

  fileDragEnded(): void {
    this.state.isDraggingFile = false;
    this.state.dragHoverFolderId = null;
  }

  folderDragEntered(folder: DriveItem): void {
    if (this.state.isDraggingFile && folder.type === 'folder' && !this.selection.isVirtualFolderId(folder.id)) {
      this.state.dragHoverFolderId = folder.id;
    }
  }

  folderDragExited(folder: DriveItem): void {
    if (this.state.dragHoverFolderId === folder.id) this.state.dragHoverFolderId = null;
  }

  isFolderDropCandidate(folder: DriveItem): boolean {
    return this.state.isDraggingFile && folder.type === 'folder' && !this.selection.isVirtualFolderId(folder.id);
  }

  nativeFileDragStarted(event: DragEvent, item: DriveItem): void {
    if (item.type === 'folder') return;
    this.state.nativeDraggedItem = item;
    this.fileDragStarted(item);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', item.id);
    }
  }

  nativeFileDragEnded(): void {
    this.state.nativeDraggedItem = undefined;
    this.fileDragEnded();
  }

  nativeFolderDragOver(event: DragEvent, folder: DriveItem): void {
    if (!this.canDropNativeFileOnFolder(folder)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this.state.dragHoverFolderId = folder.id;
  }

  nativeFolderDragLeave(folder: DriveItem): void { this.folderDragExited(folder); }

  nativeDropOnFolder(event: DragEvent, folder: DriveItem): void {
    if (!this.canDropNativeFileOnFolder(folder)) return;
    event.preventDefault();
    event.stopPropagation();
    const draggedId = event.dataTransfer?.getData('text/plain');
    const dragged = this.state.nativeDraggedItem
      || this.workspace.items.find((item) => item.id === draggedId);
    if (!dragged) return;
    const batch = this.selection.isSelected(dragged.id) ? this.selection.selectedFiles() : [dragged];
    if (this.execution.moveFiles(batch, folder)) {
      this.state.nativeDraggedItem = undefined;
      this.state.dragHoverFolderId = null;
      this.state.isDraggingFile = false;
    }
  }

  canDropNativeFileOnFolder(folder: DriveItem): boolean {
    const dragged = this.state.nativeDraggedItem;
    if (!dragged || folder.type !== 'folder' || this.selection.isVirtualFolderId(folder.id)) return false;
    const batch = this.selection.isSelected(dragged.id) ? this.selection.selectedFiles() : [dragged];
    return batch.some((file) => this.selection.canMoveFileToFolder(file, folder.id));
  }

  dropOnFolder(event: CdkDragDrop<unknown>, folder: DriveItem): void {
    this.logger.info(`[DriveViewerComponent] Drop on folder: ${JSON.stringify(folder)}`);
    const dragged = event?.item?.data as DriveItem | undefined;
    this.logger.info(`[DriveViewerComponent] Dragged item data: ${JSON.stringify(dragged)}`);
    if (!dragged) return;
    const batch = this.selection.isSelected(dragged.id) ? this.selection.selectedFiles() : [dragged];
    if (this.execution.moveFiles(batch, folder)) {
      this.state.dragHoverFolderId = null;
      this.state.isDraggingFile = false;
    }
  }
}
